import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { CurrentUserFacade } from '@navix/current-user/domain'
import { ABSOLUTE_FEATURE_URL } from '@navix/utils/tokens'
import { AuditsFacade } from '../+state/audits'
import { OrdersFacade } from '../+state/orders/orders.facade'
import { OrdersDataFilters } from '../domain/orders/data-filters.model'
import { Order } from '../domain/orders/order.model'

/**
 * @use To prevent issues injecting the ABSOLUTE_FEATURE_URL provide the service into the component that will inject providers.
 */
@Injectable()
export class PrebillNavigationService {
  private readonly router = inject(Router)
  private readonly auditsFacade = inject(AuditsFacade)
  private readonly ordersFacade = inject(OrdersFacade)
  private readonly currentUserFacade = inject(CurrentUserFacade)
  private readonly absoluteFeatureUrl = inject(ABSOLUTE_FEATURE_URL)

  nextOrder() {
    const navigationFilter: PrebillNavigationFilter = {
      navigation: 'next',
      ignoredIds: []
    }
    this.navigateOrder(navigationFilter)
  }

  previousOrder() {
    const navigationFilter: PrebillNavigationFilter = {
      navigation: 'previous',
      ignoredIds: []
    }
    this.navigateOrder(navigationFilter)
  }

  navigateOrder(navigationFilter: PrebillNavigationFilter) {
    const filters2 = this.ordersFacade.filters()
    const currentOrder = this.ordersFacade.selectedOrder()
    const nextOrder = this.getOrder(
      navigationFilter,
      this.ordersFacade.allOrders(),
      currentOrder!.id
    )

    if (nextOrder === null) {
      this.getNextOrderFromNewPage(navigationFilter, currentOrder!, filters2)
    } else this.navigatoToInvoice(nextOrder)
  }

  private getNextOrderFromNewPage(
    navigationFilter: PrebillNavigationFilter,
    currentInvoice: Order,
    filters: OrdersDataFilters
  ) {
    const basePage = currentInvoice.belongsToPage ?? (filters.page || 1)
    const newPage = this.getNewPage(navigationFilter.navigation, basePage)

    this.ordersFacade.updateListFilters({
      ...filters,
      page: newPage
    })
    this.ordersFacade.upsertOrdersPrebill({
      ...filters,
      page: newPage
    })
  }

  private getNewPage(direction: string, basePage: number) {
    switch (direction) {
      case 'next':
        return basePage + 1
      case 'previous':
        return basePage > 1 ? basePage - 1 : 1
      default:
        return 1
    }
  }

  private getOrder(
    navigationFilter: PrebillNavigationFilter,
    orders: Order[],
    orderUuid: string
  ) {
    const currentUserOwnerEmail = this.currentUserFacade.userProfile()?.email
    const navigator = navigationFilter.navigation === 'next' ? 1 : -1
    const invoiceIndex = orders.findIndex(({ id }) => id === orderUuid)

    const calculatedIndex = this.calculateOrderIndex(
      invoiceIndex,
      navigator,
      orders,
      navigationFilter.ignoredIds
    )
    const filteredInvoicesArray = orders.filter(
      order => !navigationFilter.ignoredIds.includes(order.id)
    )
    const filteredCaculatedIndex = filteredInvoicesArray.findIndex(invoice => {
      return invoice.id === orders[calculatedIndex]?.id
    })

    const ownedInvoicesIndexes = filteredInvoicesArray
      .map((order, index) => {
        const ownerEmail = this.auditsFacade.selectedAudit()?.ownerUserEmail
        const ownerUuid = this.auditsFacade.selectedAudit()?.ownerUserUuid
        return ownerUuid && ownerEmail !== currentUserOwnerEmail ? index : -1
      })
      .filter(x => x >= 0)

    const unownedIndex = this.getUnownedIndex(
      filteredCaculatedIndex,
      ownedInvoicesIndexes,
      navigator
    )

    return filteredInvoicesArray[unownedIndex] ?? null
  }

  private calculateOrderIndex(
    currentIndex: number,
    navigator: number,
    invoicesArray: Order[],
    ignoredIds: string[]
  ): number {
    const nextIndex = currentIndex + navigator
    if (ignoredIds.includes(invoicesArray[nextIndex]?.id)) {
      return this.calculateOrderIndex(
        nextIndex,
        navigator,
        invoicesArray,
        ignoredIds
      )
    }
    return nextIndex
  }

  getUnownedIndex = (
    index: number,
    indexes: number[],
    navigator: number
  ): number => {
    const isIncluded = indexes.includes(index)
    return isIncluded
      ? this.getUnownedIndex(index + navigator, indexes, navigator)
      : index
  }

  private navigatoToInvoice(nextOrder: Order | null) {
    const redirectionRoute =
      nextOrder === null
        ? [this.absoluteFeatureUrl, 'list']
        : [
            this.absoluteFeatureUrl,
            'details',
            `${nextOrder.id}_0`,
            {
              outlets: {
                tabs: ['details']
              }
            }
          ]
    this.router.navigate(redirectionRoute)
  }
}
export type PrebillNavigationFilter = {
  navigation: 'next' | 'previous'
  ignoredIds: string[]
}
